import React, { useCallback, useMemo, useState } from 'react'
import { useScreenshotStepData } from '@/tiptap/hooks/useExtensionData'
import { getStepAction } from '@deepguide-ai/shared'
import { IconCategory, IconType } from '@deepguide-ai/types'
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Dialog,
  DialogContent,
  Separator,
  Slider,
  Switch,
} from '@deepguide-ai/ui'
import { ImageViewerDialog } from '@deepguide-ai/ui/image-viewer-dialog'
import { Input } from '@deepguide-ai/ui/input'
import { Editor } from '@tiptap/core'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { NodeViewContent, NodeViewWrapper } from '@tiptap/react'
import type { InteractionPointData } from '@deepguide-ai/react-image-editor'
import { useAtom, useSetAtom } from 'jotai'
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Eye,
  Maximize2,
  MoreHorizontal,
  Plus,
  Trash2,
  ZoomInIcon,
  ZoomOutIcon,
  SquarePen,
} from 'lucide-react'
import { nanoid } from 'nanoid'
import { editedScreenshotMapAtom } from '@/stores/imageEditorStore'
import {
  isImageEditorOpenAtom,
  saveImageEditorDataAtom,
} from '@/stores/imageEditorStore'
import { cn } from '@/lib/utils'
import { IconEditor } from '@/components/ui/icon-editor'
import { EmojiPicker } from '@/components/emoji-picker'
import { IconComponent } from '@/components/icon-component'
import { FeatureGate } from '@/components/plan-restrictions/FeatureGate'
import {
  SettingsField,
  SettingsPopoverHeader,
  SettingsToggle,
} from '@/tiptap/tiptap-ui/settings-popover/settings-popover'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from './plate-ui/dropdown-menu'
import { Toolbar, ToolbarButton } from './plate-ui/toolbar'
import { InteractionPoint } from './screenshot-extension'
import { ScreenshotRenderer } from './shared/screenshot-renderer'
import { StepAttributes } from './step-extension'

// Screenshot Toolbar Component
const ScreenshotToolbar = ({
  zoomLevel,
  onZoomChange,
  onAlignChange,
  align,
  onDelete,
  interactionPoints,
  src,
  stepId,
  reviewMode = false,
  allowEditingInReview = false,
}: {
  zoomLevel: number
  onZoomChange: (zoom: number) => void
  onAlignChange: (align: 'left' | 'center' | 'right') => void
  align: 'left' | 'center' | 'right'
  onDelete: () => void
  interactionPoints: InteractionPoint[]
  src: string
  stepId: string
  /** When true, only show fullscreen button (for auditor review mode) */
  reviewMode?: boolean
  /** Recording review may edit screenshots while all other content stays read-only. */
  allowEditingInReview?: boolean
}) => {
  const alignItems = [
    { icon: AlignLeft, value: 'left' },
    { icon: AlignCenter, value: 'center' },
    { icon: AlignRight, value: 'right' },
  ]
  const IconValue =
    alignItems.find((item) => item.value === align)?.icon ?? AlignCenter

  const [_isImageEditorOpen, setIsImageEditorOpen] = useAtom(
    isImageEditorOpenAtom
  )
  const saveImageEditorData = useSetAtom(saveImageEditorDataAtom)
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false)

  const openImageEditor = useCallback(() => {
    const initialInteractionPoints: InteractionPointData[] =
      interactionPoints?.map((point) => ({
        id: point.id,
        tool: 'interactionPoint',
        x: point.xPercent,
        y: point.yPercent,
        type:
          point.type === 'Click' ||
          point.type === 'Hover' ||
          point.type === 'Input' ||
          point.type === 'Submit' ||
          point.type === 'Scroll'
            ? point.type
            : 'Click',
      })) || []

    saveImageEditorData({
      src,
      initialInteractionPoints,
      stepId,
    })
    setIsImageEditorOpen(true)
  }, [
    interactionPoints,
    saveImageEditorData,
    setIsImageEditorOpen,
    src,
    stepId,
  ])

  // Review mode is view-only except for the explicit recording-review escape hatch.
  if (reviewMode) {
    return (
      <>
        <Toolbar
          variant='media'
          className={cn(
            'absolute top-2 right-2 z-10 h-8 items-center border-none px-1 py-1 opacity-0 transition-opacity group-hover/media:opacity-100'
          )}
          contentEditable={false}
        >
          {/* Fullscreen preview - only button in review mode */}
          <ToolbarButton
            size='none'
            variant='media'
            onClick={() => setIsFullscreenOpen(true)}
            tooltip='View fullscreen'
            className='inline-flex h-6 w-6 items-center justify-center rounded-sm hover:bg-white/10'
          >
            <Maximize2 className='size-3.5' />
          </ToolbarButton>
          {allowEditingInReview && (
            <FeatureGate flag='imageEditor'>
              <ToolbarButton
                size='none'
                variant='media'
                onClick={openImageEditor}
                tooltip='Edit screenshot'
                className='inline-flex h-6 w-6 items-center justify-center rounded-sm hover:bg-white/10'
              >
                <SquarePen className='size-3.5' />
              </ToolbarButton>
            </FeatureGate>
          )}
        </Toolbar>

        <Dialog open={isFullscreenOpen} onOpenChange={setIsFullscreenOpen}>
          <DialogContent
            closeClassName='text-white'
            className='h-screen w-screen !max-w-screen rounded-none border-none bg-black/90 p-10 shadow-none'
          >
            <div className='relative mx-auto'>
              <img
                src={src}
                alt='Screenshot preview'
                className='h-auto max-h-[calc(100vh-5rem)] w-auto max-w-[calc(100vw-5rem)] object-contain'
                draggable={false}
              />
              {interactionPoints?.length > 0 &&
                interactionPoints?.map((point) => (
                  <div
                    key={point.id}
                    className='absolute size-5'
                    style={{
                      left: `${point.xPercent}%`,
                      top: `${point.yPercent}%`,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(142, 81, 255, 0.15)',
                      border: `3px solid rgb(142, 81, 255)`,
                      borderRadius: '50%',
                      boxShadow: `0 0 0 20px #8e51ff4d`,
                      zIndex: 2,
                      pointerEvents: 'none',
                    }}
                  />
                ))}
            </div>
          </DialogContent>
        </Dialog>
      </>
    )
  }

  return (
    <>
      <Toolbar
        variant='media'
        className={cn(
          'absolute top-2 right-2 z-10 h-7 border-none opacity-0 transition-opacity group-hover/media:opacity-100'
        )}
        contentEditable={false}
      >
        {/* Zoom Controls */}
        <ToolbarButton
          size='none'
          variant='media'
          onClick={() => onZoomChange(Math.max(1, zoomLevel - 0.25))}
          tooltip='Zoom out'
          disabled={zoomLevel <= 1}
          className={cn(
            'p-0.5 hover:bg-white/10',
            zoomLevel <= 1 && 'opacity-50'
          )}
        >
          <ZoomOutIcon className='size-4' />
        </ToolbarButton>
        <div className='flex items-center px-2'>
          <Slider
            value={[zoomLevel]}
            max={10}
            min={1}
            step={0.25}
            onValueChange={(value) => onZoomChange(value[0])}
            className='w-12 [&_[data-slot=slider-range]]:bg-white [&_[data-slot=slider-thumb]]:h-3 [&_[data-slot=slider-thumb]]:w-3 [&_[data-slot=slider-thumb]]:border-0 [&_[data-slot=slider-thumb]]:bg-white [&_[data-slot=slider-thumb]]:shadow-sm [&_[data-slot=slider-track]]:h-1 [&_[data-slot=slider-track]]:bg-white/30'
          />
        </div>
        <div className='flex w-8 items-center justify-center px-1 text-xs text-white'>
          {zoomLevel.toFixed(1)}x
        </div>
        <ToolbarButton
          size='none'
          variant='media'
          onClick={() => onZoomChange(Math.min(10, zoomLevel + 0.25))}
          tooltip='Zoom in'
          disabled={zoomLevel >= 10}
          className={cn(
            'p-0.5 hover:bg-white/10',
            zoomLevel >= 10 && 'opacity-50'
          )}
        >
          <ZoomInIcon className='size-4' />
        </ToolbarButton>

        {/* Fullscreen preview */}
        <ToolbarButton
          size='none'
          variant='media'
          onClick={() => setIsFullscreenOpen(true)}
          tooltip='View fullscreen'
          className='p-0.5 hover:bg-white/10'
        >
          <Eye className='size-4' />
        </ToolbarButton>

        {/* Alignment Dropdown */}
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <ToolbarButton
              size='none'
              variant='media'
              className='p-0.5 hover:bg-white/10'
              tooltip='Align'
            >
              <IconValue className='size-4' />
            </ToolbarButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='min-w-0 rounded-md border-none bg-black/60 p-0 shadow-none'
            align='end'
          >
            <DropdownMenuRadioGroup
              className='flex hover:bg-transparent'
              value={align}
              onValueChange={(value) =>
                onAlignChange(value as 'left' | 'center' | 'right')
              }
            >
              {alignItems.map(({ icon: Icon, value: itemValue }) => (
                <DropdownMenuRadioItem
                  key={itemValue}
                  className='size-[26px] text-white opacity-60 hover:opacity-100 data-[state=checked]:bg-black/5 data-[state=checked]:opacity-100'
                  value={itemValue}
                  hideIcon
                >
                  <Icon className='size-4 h-4 w-4 text-white' />
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        {/* edit screenshot */}
        <FeatureGate flag='imageEditor'>
          <ToolbarButton
            size='none'
            variant='media'
            onClick={openImageEditor}
            tooltip='Edit screenshot'
            className='p-0.5 hover:bg-white/10'
          >
            <SquarePen className='size-4' />
          </ToolbarButton>
        </FeatureGate>
        {/* Delete Button */}
        <ToolbarButton
          size='none'
          variant='media'
          onClick={onDelete}
          tooltip='Remove screenshot '
          className='p-0.5 hover:bg-red-500/20 hover:text-red-400'
        >
          <Trash2 className='size-4' />
        </ToolbarButton>
      </Toolbar>
      <ImageViewerDialog
        open={Boolean(isFullscreenOpen)}
        imageUrl={src}
        onOpenChange={(open) => {
          if (!open) {
            setIsFullscreenOpen(false)
          }
        }}
      />
      {/* <Dialog open={isFullscreenOpen} onOpenChange={setIsFullscreenOpen}>
      <DialogContent
        closeClassName='text-white'
        className='!max-w-screen w-screen border-none shadow-none h-screen p-10 rounded-none bg-black/90'
      >
        <div className='relative mx-auto'>
            <img
            src={src}
            alt='Screenshot preview'
            className='object-contain w-auto h-auto max-w-[calc(100vw-5rem)] max-h-[calc(100vh-5rem)]'
            draggable={false}
          />
          {interactionPoints?.length > 0 && interactionPoints?.map((point) => (
            <div
              key={point.id}
              className='absolute size-5'
              style={{
                left: `${point.xPercent}%`,
                top: `${point.yPercent}%`,
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(142, 81, 255, 0.15)',
                border: `3px solid rgb(142, 81, 255)`,
                borderRadius: '50%',
                boxShadow: `0 0 0 20px #8e51ff4d`,
                zIndex: 2,
                pointerEvents: 'none',
              }}
            />
            ))
          }
        </div>
      </DialogContent>
    </Dialog> */}
    </>
  )
}

interface StepNodeViewProps {
  node: ProseMirrorNode
  editor: Editor
  getPos: () => number | undefined
  updateAttributes: (attributes: Record<string, any>) => void
  deleteNode: () => void
  selected: boolean
}

export function StepNodeView({
  node,
  editor,
  getPos,
  updateAttributes,
  deleteNode,
  selected,
}: StepNodeViewProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [isEditingTitle, setIsEditingTitle] = useState(false)

  const attrs = node.attrs as StepAttributes
  const stepTitle = attrs.title || ''
  const guideAction = attrs.guideAction || ''
  const showTitle = attrs.showTitle // Default to false
  const showCaption = attrs.showCaption || false // Default to false
  const icon = attrs.icon || 'sparkles'
  const iconType = (attrs.iconType || 'REGULAR') as IconType
  const iconCategory = (attrs.iconCategory || 'LUCIDE') as IconCategory
  const iconColor = attrs.iconColor || '#3b82f6'
  const showIcon = attrs.showIcon || false
  const cropStyle = attrs.cropStyle || 'cropped'

  const [editingTitle, setEditingTitle] = useState(stepTitle)
  // Temporary state for popover editing
  const [tempTitle, setTempTitle] = useState(stepTitle)
  const [tempGuideAction, setTempGuideAction] = useState(guideAction)
  const [tempShowTitle, setTempShowTitle] = useState(showTitle)
  const [tempIcon, setTempIcon] = useState(icon)
  const [tempIconType, setTempIconType] = useState(iconType)
  const [tempIconCategory, setTempIconCategory] = useState(iconCategory)
  const [tempIconColor, setTempIconColor] = useState(iconColor)
  const [tempShowIcon, setTempShowIcon] = useState(showIcon)
  const [tempCropStyle, setTempCropStyle] = useState(cropStyle)
  const [_tempScreenshotZoomLevel, setTempScreenshotZoomLevel] = useState(
    attrs.screenshotZoomLevel || 1
  )

  // Update editing title when attrs.title changes
  React.useEffect(() => {
    if (!isEditingTitle) {
      setEditingTitle(stepTitle)
    }
  }, [stepTitle, isEditingTitle])

  // Calculate step number based on position within parent Steps container
  const stepNumber = useMemo(() => {
    try {
      const pos = getPos()
      if (pos === undefined) return attrs.number || 1

      const $pos = editor.state.doc.resolve(pos)
      let stepsNode: ProseMirrorNode | null = null
      let stepsPos = 0

      // Walk up the tree to find the Steps parent
      for (let depth = $pos.depth; depth > 0; depth--) {
        const node = $pos.node(depth)
        if (node.type.name === 'Steps') {
          stepsNode = node
          stepsPos = $pos.before(depth)
          break
        }
      }

      if (!stepsNode) return attrs.number || 1

      // Count Step nodes within the Steps container
      let stepCount = 0
      stepsNode.forEach((child, offset) => {
        const childPos = stepsPos + offset + 1
        if (child.type.name === 'Step' && childPos < pos) {
          stepCount++
        }
      })

      const num = stepCount + 1
      if (num !== attrs.number) {
        updateAttributes({ number: num })
      }
      return num
    } catch (error) {
      console.error('Error calculating step number', error)
      return attrs.number || 1
    }
  }, [editor.state.doc, getPos, updateAttributes, attrs.number])

  // Extract caption text from first paragraph in node content
  // NOTE: Must be after stepNumber calculation since it depends on stepNumber
  const captionText = useMemo(() => {
    if (!showCaption) {
      return ''
    }

    if (attrs.title) {
      return attrs.title
    }

    if (!node.content || node.content.size === 0) {
      return ''
    }

    // Find first paragraph node
    let firstParagraphText = ''
    node.content.forEach((child) => {
      if (child.type.name === 'paragraph' && !firstParagraphText) {
        // Extract text content from paragraph
        child.content?.forEach((textNode) => {
          if (textNode.type.name === 'text' && textNode.text) {
            firstParagraphText += textNode.text
          }
        })
      }
    })

    // Limit to 150 characters
    const caption = firstParagraphText.substring(0, 150)
    return caption
  }, [node.content, showCaption, attrs.title])

  // Function to save settings from popover
  const saveSettings = useCallback(() => {
    const newAttributes: Partial<StepAttributes> = {}

    if (tempTitle.trim() !== stepTitle)
      newAttributes.title = tempTitle.trim() || ''
    if (tempGuideAction.trim() !== guideAction)
      newAttributes.guideAction = tempGuideAction.trim() || ''
    if (tempShowTitle !== showTitle) newAttributes.showTitle = tempShowTitle
    if (tempIcon !== icon) newAttributes.icon = tempIcon
    if (tempIconType !== iconType) newAttributes.iconType = tempIconType
    if (tempIconCategory !== iconCategory)
      newAttributes.iconCategory = tempIconCategory
    if (tempIconColor !== iconColor) newAttributes.iconColor = tempIconColor

    if (tempShowIcon !== showIcon) {
      newAttributes.showIcon = tempShowIcon
      if (tempShowIcon) {
        if (!newAttributes.icon) newAttributes.icon = 'sparkles'
        if (!newAttributes.iconType) newAttributes.iconType = 'REGULAR'
        if (!newAttributes.iconCategory) newAttributes.iconCategory = 'LUCIDE'
        if (!newAttributes.iconColor) newAttributes.iconColor = '#3b82f6'
      }
    }

    if (tempCropStyle !== cropStyle) newAttributes.cropStyle = tempCropStyle

    if (Object.keys(newAttributes).length > 0) {
      updateAttributes(newAttributes)
    }
    setIsPopoverOpen(false)
  }, [
    tempTitle,
    stepTitle,
    tempGuideAction,
    guideAction,
    tempShowTitle,
    showTitle,
    tempIcon,
    icon,
    tempIconType,
    iconType,
    tempIconCategory,
    iconCategory,
    tempIconColor,
    iconColor,
    tempShowIcon,
    showIcon,
    tempCropStyle,
    cropStyle,
    updateAttributes,
  ])

  // Function to cancel settings changes
  const cancelSettings = useCallback(() => {
    setTempTitle(stepTitle)
    setTempGuideAction(guideAction)
    setTempShowTitle(showTitle)
    setTempIcon(icon)
    setTempIconType(iconType)
    setTempIconCategory(iconCategory)
    setTempIconColor(iconColor)
    setTempShowIcon(showIcon)
    setTempCropStyle(cropStyle)
    setTempScreenshotZoomLevel(attrs.screenshotZoomLevel || 1)
    setIsPopoverOpen(false)
  }, [
    stepTitle,
    guideAction,
    showTitle,
    icon,
    iconType,
    iconCategory,
    iconColor,
    showIcon,
    cropStyle,
    attrs.screenshotZoomLevel,
  ])

  // Function to open settings popover
  const openSettings = useCallback(() => {
    setTempTitle(stepTitle)
    setTempGuideAction(guideAction)
    setTempShowTitle(showTitle)
    setTempIcon(icon)
    setTempIconType(iconType)
    setTempIconCategory(iconCategory)
    setTempIconColor(iconColor)
    setTempShowIcon(showIcon)
    setTempCropStyle(cropStyle)
    setTempScreenshotZoomLevel(attrs.screenshotZoomLevel || 1)
    setIsPopoverOpen(true)
  }, [
    stepTitle,
    guideAction,
    showTitle,
    icon,
    iconType,
    iconCategory,
    iconColor,
    showIcon,
    cropStyle,
    attrs.screenshotZoomLevel,
  ])

  // Function to handle adding a new step after this one
  const handleAddStep = useCallback(() => {
    const pos = getPos()
    if (pos === undefined) return

    editor.commands.insertContentAt(pos + node.nodeSize, {
      type: 'Step',
      attrs: {
        id: nanoid(10),
        title: `Step ${stepNumber + 1}`,
        showTitle: true,
        showIcon: false,
      },
      content: [
        {
          type: 'paragraph',
          content: [],
        },
      ],
    })
  }, [editor, getPos, node.nodeSize, stepNumber])

  // Function to handle deleting this step
  const handleDeleteStep = useCallback(() => {
    deleteNode()
  }, [deleteNode])

  // Function to start editing the title
  const handleTitleClick = useCallback(() => {
    if (editor.isEditable) {
      setIsEditingTitle(true)
      setEditingTitle(stepTitle)
    }
  }, [editor.isEditable, stepTitle])

  // Function to save the edited title
  const handleTitleSave = useCallback(() => {
    if (editingTitle.trim() !== stepTitle) {
      updateAttributes({ title: editingTitle.trim() || 'Step' })
    }
    setIsEditingTitle(false)
  }, [editingTitle, stepTitle, updateAttributes])

  // Function to cancel editing
  const handleTitleCancel = useCallback(() => {
    setEditingTitle(stepTitle)
    setIsEditingTitle(false)
  }, [stepTitle])

  // Function to handle key press in title input
  const handleTitleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleTitleSave()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        handleTitleCancel()
      }
    },
    [handleTitleSave, handleTitleCancel]
  )

  const stepId = attrs.id || nanoid(10)

  // 获取全局已编辑截图映射
  const [editedMap] = useAtom(editedScreenshotMapAtom)
  // Fetch screenshot data only for likely DB IDs (avoid fetching for human-friendly IDs like "step-save-todo")
  // dE3G5MkCp6r_MG_IJ_fvu
  const isLikelyDbId =
    !!stepId && (stepId.length === 21 || stepId.startsWith('step-')) // && /^[a-z0-9\\-_]{21,}$/.test(stepId);
  const shouldFetchStep = !!stepId && isLikelyDbId && cropStyle !== 'none'
  const { step: stepData, isLoading: isLoadingStep } = useScreenshotStepData({
    stepId: shouldFetchStep ? stepId : undefined,
    enabled: shouldFetchStep,
  })

  // A retake replaces the source image, so its previous local edit is no longer valid.
  const cachedEdit = editedMap[stepId]
  const editedScreenshot = !stepData?.screenshot?.url || cachedEdit?.sourceUrl === stepData.screenshot.url
    ? cachedEdit
    : undefined

  // 优先使用保存后的 editedUrl（包括后端返回的 editedUrl），其次节点属性上的 screenshotUrl，最后后端原始 url
  const screenshotUrl =
    editedScreenshot?.url ||
    stepData?.screenshot?.editedUrl ||
    attrs.screenshotUrl ||
    stepData?.screenshot?.url
  // Use viewportInfo dimensions for consistent aspect ratio with interaction points
  // This ensures the container matches the original capture viewport
  const isFullPage = (attrs.screenshotUrl ? attrs.screenshotScope : stepData?.screenshotScope ?? attrs.screenshotScope) === 'FULL_PAGE'
  const screenshotWidth =
    attrs.screenshotWidth ||
    (isFullPage ? stepData?.screenshot?.width : stepData?.viewportInfo?.width) ||
    stepData?.screenshot?.width ||
    800
  const screenshotHeight =
    attrs.screenshotHeight ||
    (isFullPage ? stepData?.screenshot?.height : stepData?.viewportInfo?.height) ||
    stepData?.screenshot?.height ||
    600
  // 优先使用 editedMap 中的 viewportCoordinates，其次使用 stepData 中的，
  // 最后回退到节点 attrs 上预注入的 elementBox（服务端 inject-step-screenshots 写入）。
  const elementBox =
    editedScreenshot?.viewportCoordinates ||
    stepData?.viewportCoordinates ||
    attrs.elementBox

  // Build interaction points if available. We prefer the live-fetched stepData
  // (so user edits in editedMap take effect), and fall back to attrs that were
  // pre-computed on the server when no `stepByIdApiResolver` is configured for
  // this surface (e.g., the recording editor / public viewer).
  const interactionPoints: InteractionPoint[] = useMemo(() => {
    if (isFullPage) return []
    if (stepData?.viewportInfo) {
      const viewportCoordinates =
        editedScreenshot?.viewportCoordinates || stepData?.viewportCoordinates
      if (!viewportCoordinates) return []

      const refWidth = stepData.viewportInfo.width
      const refHeight = stepData.viewportInfo.height

      return [
        {
          id: stepData.id,
          type: stepData.action || 'Click',
          x: viewportCoordinates.center.x,
          y: viewportCoordinates.center.y,
          width: viewportCoordinates.width,
          height: viewportCoordinates.height,
          xPercent: (viewportCoordinates.center.x / refWidth) * 100,
          yPercent: (viewportCoordinates.center.y / refHeight) * 100,
          widthPercent: (viewportCoordinates.width / refWidth) * 100,
          heightPercent: (viewportCoordinates.height / refHeight) * 100,
          label: getStepAction(stepData) ?? '',
          style: stepData.action,
        },
      ]
    }

    if (
      Array.isArray(attrs.interactionPoints) &&
      attrs.interactionPoints.length > 0
    ) {
      const editedCoordinates = editedScreenshot?.viewportCoordinates
      if (
        editedCoordinates &&
        attrs.screenshotWidth &&
        attrs.screenshotHeight
      ) {
        return attrs.interactionPoints.map((point) => ({
          ...point,
          x: editedCoordinates.center.x,
          y: editedCoordinates.center.y,
          width: editedCoordinates.width,
          height: editedCoordinates.height,
          xPercent:
            (editedCoordinates.center.x / attrs.screenshotWidth) * 100,
          yPercent:
            (editedCoordinates.center.y / attrs.screenshotHeight) * 100,
          widthPercent: (editedCoordinates.width / attrs.screenshotWidth) * 100,
          heightPercent:
            (editedCoordinates.height / attrs.screenshotHeight) * 100,
        }))
      }
      return attrs.interactionPoints as InteractionPoint[]
    }

    return []
  }, [
    stepData,
    editedScreenshot,
    stepId,
    attrs.interactionPoints,
    isFullPage,
    attrs.screenshotWidth,
    attrs.screenshotHeight,
  ])

  // Update node attributes when screenshot data is loaded from database
  React.useEffect(() => {
    if (!stepData?.screenshot?.url || isLoadingStep || !editor.isEditable)
      return

    // Use setTimeout to ensure the update happens after the editor is ready
    const timeoutId = setTimeout(() => {
      const pos = getPos()
      if (pos === undefined) return

      const updates: Partial<StepAttributes> = {}
      let hasUpdates = false

      // Update screenshot URL if not already set (prefer editedUrl)
      const preferredUrl =
        stepData.screenshot.editedUrl || stepData.screenshot.url
      if (!attrs.screenshotUrl && preferredUrl) {
        updates.screenshotUrl = preferredUrl
        hasUpdates = true
      }

      if (isFullPage && attrs.screenshotScope !== 'FULL_PAGE') {
        updates.screenshotScope = 'FULL_PAGE'
        updates.cropStyle = 'full'
        updates.interactionPoints = []
        updates.elementBox = null
        hasUpdates = true
      }

      // Update width if not already set
      if (stepData.screenshot.width && !attrs.screenshotWidth) {
        updates.screenshotWidth = stepData.screenshot.width
        hasUpdates = true
      }

      // Update height if not already set
      if (stepData.screenshot.height && !attrs.screenshotHeight) {
        updates.screenshotHeight = stepData.screenshot.height
        hasUpdates = true
      }

      // Update interaction points if not already set
      if (interactionPoints.length > 0 && !attrs.interactionPoints) {
        updates.interactionPoints = interactionPoints
        hasUpdates = true
      }

      // Update element box if not already set
      if (!isFullPage && elementBox && !attrs.elementBox) {
        updates.elementBox = elementBox
        hasUpdates = true
      }

      // console.log('hasUpdates', updates, attrs)
      if (hasUpdates) {
        updateAttributes(updates)
      }
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [
    stepData,
    isLoadingStep,
    isFullPage,
    attrs.screenshotScope,
    attrs.screenshotUrl,
    attrs.screenshotWidth,
    attrs.screenshotHeight,
    attrs.interactionPoints,
    attrs.elementBox,
    interactionPoints,
    elementBox,
    updateAttributes,
    editor.isEditable,
    getPos,
  ])

  return (
    <NodeViewWrapper
      className={cn(
        'group/step relative mb-5',
        selected && 'ProseMirror-selectednode'
      )}
      data-type='Step'
      data-dg-id={stepId}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Action buttons - appears on hover (hidden in review mode) */}
      {/* {editor.isEditable && !(editor as any).reviewMode && isHovered && (
        <div
          contentEditable={false}
          className={`pointer-events-auto absolute -top-0.5 -right-14 z-10 flex h-full gap-0 p-1 pl-4 transition-opacity ${
            isHovered  ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <Button variant='ghost' size='menuAction' onClick={handleAddStep}>
            <Plus className='h-4 w-4' />
          </Button>
        </div>
      )} */}

      {/* Step content */}
      <div className='step-content relative'>
        {/* Step header with number and title */}
        <Separator
          orientation='vertical'
          className='step-line absolute bottom-[-8px] left-[14px] !h-[calc(100%-36px)]'
        />
        <div className='mb-4 flex items-start gap-5'>
          {/* Step number circle */}
          <div className='bg-muted text-foreground flex h-7 w-7 shrink-0 items-center justify-center rounded-sm text-sm font-medium'>
            {/* Action buttons - appears on hover (hidden in review mode)   */}
            {editor.isEditable &&
            !(editor as any).reviewMode &&
            (isHovered || isPopoverOpen) ? (
              <div
                contentEditable={false}
                className={`pointer-events-auto z-10 flex h-full gap-0 transition-opacity ${
                  isHovered || isPopoverOpen ? 'opacity-100' : 'opacity-0'
                }`}
              >
                {/* Settings Popover */}
                <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant='ghost'
                      size='menuAction'
                      className='h-7 w-7'
                      onClick={openSettings}
                    >
                      <MoreHorizontal className='h-4 w-4' />
                    </Button>
                  </PopoverTrigger>

                  <PopoverContent
                    className='w-80 p-4 pb-3'
                    align='start'
                    side='bottom'
                    sideOffset={8}
                  >
                    <div className='space-y-4'>
                      <SettingsPopoverHeader
                        title='Step Settings'
                        onClose={cancelSettings}
                      />

                      {/* Show Title Toggle */}
                      <SettingsToggle label='Show Title'>
                        <Switch
                          checked={tempShowTitle}
                          onCheckedChange={() =>
                            setTempShowTitle(!tempShowTitle)
                          }
                        />
                      </SettingsToggle>

                      {/* Title Input */}
                      <SettingsField label='Title'>
                        <div className='flex items-center gap-2'>
                          <EmojiPicker
                            selectedIcon={tempShowIcon ? tempIcon : undefined}
                            iconColor={tempIconColor}
                            iconCategory={tempIconCategory}
                            onEmojiSelect={(emoji) => {
                              setTempShowIcon(true)
                              setTempIcon(emoji)
                              setTempIconCategory('EMOJI')
                            }}
                            onIconChange={(
                              iconName: string,
                              iconCategory: IconCategory
                            ) => {
                              if (iconName === '') {
                                setTempIconCategory('LUCIDE')
                                setTempShowIcon(false)
                                return
                              }
                              setTempShowIcon(true)
                              setTempIcon(iconName)
                              setTempIconCategory(iconCategory)
                            }}
                            onColorChange={setTempIconColor}
                            isPopoverMode={true}
                          />
                          <Input
                            type='text'
                            value={tempTitle}
                            onChange={(e) => setTempTitle(e.target.value)}
                            placeholder='Enter step title...'
                            className='flex-1'
                          />
                        </div>
                      </SettingsField>

                      {/* <div className='space-y-5'>
                        <div className='flex items-center justify-between'>
                          <label className='text-muted-foreground text-xs leading-4'>
                            Show Icon
                          </label>
                          <Switch
                            checked={tempShowIcon}
                            onCheckedChange={() => setTempShowIcon(!tempShowIcon)}
                          />
                        </div>

      s                  {tempShowIcon && (
                          <div onClick={(e) => e.stopPropagation()}>
                            <IconEditor
                              selectedIcon={tempIcon}
                              iconType={tempIconType}
                              iconCategory={tempIconCategory}
                              iconColor={tempIconColor}
                              onIconChange={setTempIcon}
                              onTypeChange={setTempIconType}
                              onCategoryChange={setTempIconCategory}
                              onColorChange={setTempIconColor}
                            />
                          </div>
                        )}
                      </div> */}
                      {/* <div className='flex flex-col gap-2'>
                        <label className='text-muted-foreground text-xs leading-4'>
                          Icon
                        </label>
                        <EmojiPicker
                          selectedIcon={tempShowIcon ? tempIcon : undefined}
                          iconColor={tempIconColor}
                          iconCategory={tempIconCategory}
                          onEmojiSelect={(emoji) => {
                            setTempShowIcon(true)
                            setTempIcon(emoji)
                            setTempIconCategory('EMOJI')
                          }}
                          onIconChange={(
                            iconName: string,
                            iconCategory: IconCategory
                          ) => {
                            if (iconName === '') {
                              setTempIconCategory('LUCIDE')
                              setTempShowIcon(false)
                              return
                            }
                            setTempShowIcon(true)
                            setTempIcon(iconName)
                            setTempIconCategory(iconCategory)
                          }}
                          onColorChange={setTempIconColor}
                          isPopoverMode={true}
                        />
                      </div> */}

                      {/* Screenshot Crop Style */}
                      {/* 暂时先注释 */}
                      {/* <div className='flex flex-col gap-2'>
                        <label className='text-muted-foreground text-xs leading-4'>
                          Screenshot Display
                        </label>
                        <Select
                          value={tempCropStyle}
                          onValueChange={(value: 'cropped' | 'full' | 'none') =>
                            setTempCropStyle(value)
                          }
                        >
                          <SelectTrigger className='h-9 w-full'>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='cropped'>Cropped (Default)</SelectItem>
                            <SelectItem value='full'>Full Screenshot</SelectItem>
                            <SelectItem value='none'>No Screenshot</SelectItem>
                          </SelectContent>
                        </Select>
                      </div> */}

                      <Separator className='mt-5 mb-3 -ml-4 !w-[calc(100%32px)]' />

                      {/* Action Buttons */}
                      <div className='flex items-center justify-between'>
                        <Button
                          variant='destructive'
                          className='h-8 !bg-transparent px-2 py-1.5 text-[var(--destructive)] shadow-none'
                          size='sm'
                          onClick={handleDeleteStep}
                        >
                          Delete
                        </Button>
                        <div className='flex items-center space-x-2'>
                          <Button
                            variant='outline'
                            size='sm'
                            onClick={cancelSettings}
                          >
                            Cancel
                          </Button>
                          <Button size='sm' onClick={saveSettings}>
                            Save Changes
                          </Button>
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            ) : (
              stepNumber
            )}
          </div>

          {/* Title and icon */}
          <div className='flex flex-1 flex-col'>
            <div className='flex items-start gap-2'>
              {showIcon && (
                <IconComponent
                  iconName={icon}
                  iconType={iconType}
                  iconCategory={iconCategory}
                  iconColor={iconColor}
                  size={20}
                  className={`mt-1 mb-5 transition-colors ${
                    iconCategory === 'EMOJI' ? 'h-[20px] text-[20px]' : ''
                  }`}
                />
              )}
              {showTitle && (
                <div className='mb-4 !ml-0'>
                  {isEditingTitle ? (
                    <input
                      type='text'
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onBlur={handleTitleSave}
                      onKeyDown={handleTitleKeyDown}
                      autoFocus
                      className='w-full border-none bg-transparent text-[20px] !leading-7 font-semibold outline-none'
                      placeholder='Enter step title...'
                    />
                  ) : (
                    <h3
                      className='hover:bg-accent -mx-1 !mb-0 cursor-pointer rounded-sm px-1 text-base !leading-7 font-semibold transition-colors'
                      onClick={handleTitleClick}
                      contentEditable={false}
                    >
                      {stepTitle || 'Click to add title'}
                    </h3>
                  )}
                </div>
              )}
            </div>
            {/* {guideAction && (
              <div className="text-sm text-muted-foreground mt-1 ml-0">{guideAction}</div>
            )} */}
            {/* Step body content */}
            <div className='step-content-body'>
              <NodeViewContent />
            </div>

            {/* Screenshot */}
            {screenshotUrl && (
              <div className='group/media relative mt-4'>
                <ScreenshotRenderer
                  url={screenshotUrl}
                  width={screenshotWidth}
                  height={screenshotHeight}
                  elementBox={isFullPage ? undefined : elementBox}
                  interactionPoints={interactionPoints}
                  cropStyle={isFullPage ? 'full' : cropStyle}
                  align={attrs.align || 'center'}
                  alt={`Screenshot for ${stepTitle || 'step'}`}
                  zoomLevel={attrs.screenshotZoomLevel || 1}
                  panX={attrs.screenshotPanX ?? 0}
                  panY={attrs.screenshotPanY ?? 0}
                  onZoomChange={(zoom) => {
                    updateAttributes({
                      screenshotZoomLevel: zoom,
                      // Reset pan when zoom changes to 1 or below
                      ...(zoom <= 1
                        ? { screenshotPanX: 0, screenshotPanY: 0 }
                        : {}),
                    })
                  }}
                  onAlignChange={(align) => updateAttributes({ align })}
                  onWidthChange={(newWidth) => {
                    const newHeight =
                      (newWidth / screenshotWidth) * screenshotHeight
                    updateAttributes({
                      screenshotWidth: newWidth,
                      screenshotHeight: newHeight,
                    })
                  }}
                  onPanChange={(panX, panY) => {
                    updateAttributes({
                      screenshotPanX: panX,
                      screenshotPanY: panY,
                    })
                  }}
                  readOnly={!editor.isEditable || !!(editor as any).reviewMode}
                  isFullScreenshot={true}
                />
                {/* Screenshot Toolbar - shows only fullscreen button in review mode */}
                {(editor.isEditable || (editor as any).reviewMode) && (
                  <ScreenshotToolbar
                    zoomLevel={attrs.screenshotZoomLevel || 1}
                    onZoomChange={(zoom) => {
                      updateAttributes({
                        screenshotZoomLevel: zoom,
                        // Reset pan when zoom changes to 1 or below
                        ...(zoom <= 1
                          ? { screenshotPanX: 0, screenshotPanY: 0 }
                          : {}),
                      })
                    }}
                    onAlignChange={(align) => updateAttributes({ align })}
                    align={attrs.align || 'center'}
                    onDelete={() => {
                      // Remove the screenshot attributes but keep the step
                      updateAttributes({
                        screenshotUrl: null,
                        screenshotWidth: null,
                        screenshotHeight: null,
                        cropStyle: 'none',
                      })
                    }}
                    interactionPoints={interactionPoints}
                    src={screenshotUrl}
                    stepId={attrs.id || stepId}
                    reviewMode={(editor as any).reviewMode || false}
                    allowEditingInReview={
                      !!(editor as any).allowScreenshotEditingInReview
                    }
                  />
                )}
                {attrs.pageUrl && (
                  <div
                    className='text-text-subtle mt-1 truncate text-[11px]'
                    contentEditable={false}
                  >
                    <a
                      href={attrs.pageUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='hover:text-cobalt-strong hover:underline'
                      title={attrs.pageUrl}
                    >
                      {attrs.pageUrl}
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Caption below screenshot (PDF-only) */}
            {screenshotUrl && showCaption && captionText && (
              <div
                className='mt-2 text-center text-sm text-gray-600 italic'
                contentEditable={false}
              >
                <span className='font-semibold text-gray-900'>
                  Step {stepNumber}:
                </span>{' '}
                {captionText}
              </div>
            )}

            {/* Loading placeholder with proper dimensions */}
            {!screenshotUrl && isLoadingStep && (
              <div
                className='bg-muted/40 border-border mt-4 flex items-center justify-center rounded-md border-2 border-dashed'
                style={{
                  width: screenshotWidth || 800,
                  height: screenshotHeight || 600,
                  maxWidth: '100%',
                  aspectRatio: `${screenshotWidth || 800} / ${screenshotHeight || 600}`,
                }}
              >
                <div className='text-muted-foreground text-sm'>
                  Loading screenshot...
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </NodeViewWrapper>
  )
}
